/**
 * IMPORTS
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";

// typings
import {
  IAtualizarModeloPayload,
  IAtualizarModeloResponse,
} from "@/hooks/modelos/atualizar-modelo/index.d";

// store / zustand
import { useStoreZustandUserAuth } from "@/store-zustand/user-auth";

// domain / caso de usos
import { handleAtualizarModelo } from "@/domain/use-cases/modelos";

const notifySuccess = (message: string) =>
  toast(message, {
    type: "success",
    position: "top-center",
  });

const notifyError = (message: string) =>
  toast(message, {
    type: "error",
    position: "top-center",
  });

const notifyWarning = (message: string) =>
  toast(message, {
    type: "warning",
    position: "top-center",
  });

/**
 * Função que atualizar um modelo de um usuário (cliente)
 * @param user_id id do usuário dono do modelo
 * @returns
 */
export function useAtualizarModelo() {
  const queryClient = useQueryClient(); // ← necessário para invalidar a query
  const { token } = useStoreZustandUserAuth();

  const mutation = useMutation<IAtualizarModeloResponse, Error, IAtualizarModeloPayload>({
    mutationFn: async ({ payload, modelo_id }) => {
      const formData = new FormData();

      if (payload) {
        // formData.append("cliente_id", String(payload?.user_id));
        formData.append("produto_id", String(payload?.produto_id));
        formData.append("nome_modelo", payload?.nome_modelo);
        formData.append("campo_chave", payload.campo_chave);
        formData.append("posicionamento", payload.posicionamento.toLocaleLowerCase());
        formData.append("tem_furo", payload.tem_furo ? "1" : "0");
        formData.append("tem_carga_foto", payload.tem_carga_foto ? "1" : "0");
        formData.append("tem_dados_variaveis", payload.tem_dados_variaveis ? "1" : "0");
        if (payload.is_provisorio !== undefined) {
          formData.append("is_provisorio", payload.is_provisorio ? "1" : "0");
        }
        formData.append("tecnologia_id", String(payload.tecnologia_id)); // ou enviar múltiplos com JSON.stringify se backend aceitar

        // if (payload.tipo_furo) formData.append("tipo_furo", payload.tipo_furo);
        if (payload.observacoes) {
          formData.append("observacoes", payload.observacoes);
        }

        // Se o tipo_furo for "1", adiciona ao FormData
        if (payload.tem_furo) {
          formData.append("tipo_furo", payload.tipo_furo!);
        }

        if (payload.foto_frente) {
          formData.append("foto_frente", payload.foto_frente);
        }

        if (payload.foto_verso) {
          formData.append("foto_verso", payload.foto_verso);
        }
      }

      const response = await handleAtualizarModelo(token!, modelo_id, formData as any);

      // o caso de uso devolve o erro do Axios em vez de lançar; sem lançar aqui o
      // mutateAsync resolve e o modal segue como se o modelo tivesse sido salvo
      if (!response?.data?.id) {
        throw response instanceof Error ? response : new Error("Falha ao atualizar o modelo");
      }

      return response;
    },
    onSuccess: async () => {
      notifySuccess("Modelo atualizado com sucesso!");

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["modelos-por-cliente"] }),
        queryClient.invalidateQueries({ queryKey: ["modelos"] }),
        queryClient.invalidateQueries({ queryKey: ["modelos-unico"] }),
      ]);
    },
    onError: async (error) => {
      if (error?.message === "Request failed with status code 404") {
        notifyWarning("Modelo não encontrado!");
        return;
      }

      notifyError("Houve um problema em atualizar o modelo!");
    },
  });

  return {
    atualizarModelo: mutation.mutate, // chamada direta
    atualizarModeloAsync: mutation.mutateAsync, // se quiser await
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
    data: mutation.data,
    isSuccess: mutation.isSuccess,
  };
}
